/**
 * @file: useContactPicker.ts
 *
 * All the contact-picker's state + actions: the search query, the selected
 * member set (with an optional locked seed member), who to exclude, the
 * filtered list, and the create-group / invite-members submit. Keeps the
 * page component purely presentational.
 */

import { useChatContacts } from '@euler/app/flows/chat/lib/contacts/context';
import {
  ChatContactPickerParams,
  ChatPickerDoneResult,
} from '@euler/app/flows/chat/lib/types';
import { useAlert } from '@euler/functions/ux/useAlert';
import { ChatContact, ChatRoomMemberInput } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { idEquals } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface ContactPicker {
  query: string;
  setQuery: (q: string) => void;
  visibleContacts: ChatContact[];
  /** Single-tap-to-resolve (no checkboxes / submit footer). */
  isDirect: boolean;
  selectedIds: Set<number>;
  selectedCount: number;
  lockedMemberId: number | null;
  busy: boolean;
  headerTitle: string;
  toggleContact: (memberId: number) => void;
  onSubmit: () => void;
}

export function useContactPicker(
  params: ChatContactPickerParams,
  onDone: (result: ChatPickerDoneResult) => void,
): ContactPicker {
  const { chatService } = useServiceFactory();
  // `Alert.alert` is a no-op on RN web, so validation/error feedback here must
  // route through `useAlert` (renders the `_alert` screen on web).
  const alert = useAlert();
  const { chatContacts$, refreshChatContacts } = useChatContacts();
  const [allContacts] = useBehaviorSubject(chatContacts$);

  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const isDirect = params.mode.kind === 'direct';

  useEffect(() => {
    if (allContacts.length === 0) {
      void refreshChatContacts(() => chatService.listContacts());
    }
  }, [chatService, allContacts.length, refreshChatContacts]);

  const seedMemberId = useMemo(() => {
    if (params.mode.kind !== 'new-group') return null;
    const pid = params.mode.seedPlatformUserId;
    if (!pid) return null;
    const found = allContacts.find(c => idEquals(c.platformUserId, pid));
    return found?.memberId ?? null;
  }, [params.mode, allContacts]);

  useEffect(() => {
    if (seedMemberId == null) return;
    setSelectedIds(prev => {
      if (prev.has(seedMemberId)) return prev;
      const next = new Set(prev);
      next.add(seedMemberId);
      return next;
    });
  }, [seedMemberId]);

  const lockedMemberId = seedMemberId;

  const excludedPlatformUserIds = useMemo(() => {
    if (params.mode.kind !== 'invite') return new Set<string>();
    return new Set(params.mode.existingMemberPlatformUserIds.map(String));
  }, [params.mode]);

  // Precompute pinyin initials once per directory so "李林" matches "ll".
  const indexed = useMemo(
    () =>
      allContacts.map(c => ({ c, py: getPyInitials(c.name).toLowerCase() })),
    [allContacts],
  );

  const visibleContacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return indexed
      .filter(({ c }) => !excludedPlatformUserIds.has(String(c.platformUserId)))
      .filter(
        ({ c, py }) =>
          !q ||
          py.includes(q) ||
          c.name.toLowerCase().includes(q) ||
          (c.jobTitle ?? '').toLowerCase().includes(q),
      )
      .map(({ c }) => c);
  }, [indexed, query, excludedPlatformUserIds]);

  const toggleContact = useCallback(
    (memberId: number) => {
      // Direct mode resolves on the first tap — no accumulation.
      if (isDirect) {
        const contact = allContacts.find(c => c.memberId === memberId);
        if (contact) onDone({ kind: 'direct', contact });
        return;
      }
      if (lockedMemberId === memberId) return;
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(memberId)) next.delete(memberId);
        else next.add(memberId);
        return next;
      });
    },
    [isDirect, allContacts, onDone, lockedMemberId],
  );

  const onSubmit = useCallback(async () => {
    if (busy || isDirect) return;
    const selectedContacts = allContacts.filter(c =>
      selectedIds.has(c.memberId),
    );
    if (params.mode.kind === 'new-group') {
      if (selectedContacts.length < 2) {
        await alert({
          title: '提示',
          message: params.mode.seedPlatformUserId
            ? '请再选择至少一位成员组建群聊'
            : '请选择至少两位成员组建群聊',
        });
        return;
      }
    } else {
      if (selectedContacts.length === 0) {
        await alert({ title: '提示', message: '请选择要邀请的成员' });
        return;
      }
    }

    setBusy(true);
    try {
      if (params.mode.kind === 'new-group') {
        const members: ChatRoomMemberInput[] = selectedContacts.map(c => ({
          type: 'org_member',
          memberId: c.memberId,
        }));
        // Don't send a custom title — the backend composes the room's
        // `default_title` from member names, and each viewer strips their
        // own name at render. The locally-composed string (which already
        // excludes the creator) is only used as the immediate
        // navigation/display title until the thread list reloads.
        const title = selectedContacts
          .map(c => c.name)
          .filter(Boolean)
          .join('、');
        const result = await chatService.createRoom({
          groupChatType: 'default',
          members,
        });
        onDone({ kind: 'new-group', roomId: result.roomId, title });
      } else if (params.mode.kind === 'invite') {
        const roomId = params.mode.roomId;
        const members: ChatRoomMemberInput[] = selectedContacts.map(c => ({
          type: 'org_member',
          memberId: c.memberId,
        }));
        await chatService.inviteRoomMembers(roomId, members);
        onDone({ kind: 'invite' });
      }
    } catch (e) {
      console.error('contact picker submit failed', e);
      await alert({ title: '操作失败', message: String(e) });
    } finally {
      setBusy(false);
    }
  }, [
    allContacts,
    busy,
    isDirect,
    onDone,
    params.mode,
    selectedIds,
    chatService,
    alert,
  ]);

  const headerTitle =
    params.mode.kind === 'direct'
      ? '通讯录'
      : params.mode.kind === 'new-group'
      ? '发起群聊'
      : '邀请成员';

  return {
    query,
    setQuery,
    visibleContacts,
    isDirect,
    selectedIds,
    selectedCount: selectedIds.size,
    lockedMemberId,
    busy,
    headerTitle,
    toggleContact,
    onSubmit,
  };
}
